import React, { useEffect, useState } from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import TextField from "@mui/material/TextField";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers-pro";

import LeaseEventTable from "../LeaseEventsTable";

import { ButtonContainer, Container } from "./styles";

import colors from "styles/colors";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1200,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const SnoozeIgnoreModal = (props) => {
  const {
    open,
    onClose,
    selectedLeaseEvents,
    actionType,
    onApply = () => {},
  } = props;

  const [leaseEventsToSnoozeIgnore, setLeaseEventsToSnoozeIgnore] = useState(
    []
  );

  const [snoozedUntil, setSnoozedUntil] = useState(moment().add(30, "days"));
  const [snoozeIgnoreComment, setSnoozeIgnoreComment] = useState("");

  useEffect(() => {
    setLeaseEventsToSnoozeIgnore(selectedLeaseEvents);

    setSnoozedUntil(moment().add(30, "days"));
    setSnoozeIgnoreComment("");
  }, [selectedLeaseEvents]);

  const handleDelete = (leaseEventId) => {
    setLeaseEventsToSnoozeIgnore([
      ...leaseEventsToSnoozeIgnore.filter(
        (l) => l.leaseEventId !== leaseEventId
      ),
    ]);
  };

  const renderTitle = () => {
    switch (actionType) {
      case "snooze":
        return "SNOOZE LEASE EVENTS";

      case "ignore":
        return "IGNORE LEASE EVENTS";

      case "unsnooze":
        return "UNSNOOZE LEASE EVENTS";

      case "unignore":
        return "UNIGNORE LEASE EVENTS";
    }

    return "LEASE EVENTS";
  };

  const renderSubtitle = () => {
    switch (actionType) {
      case "snooze":
        return "Snoozing these lease events will remove them from the upcoming lease events list until the specified snooze date.";

      case "ignore":
        return "Ignoring these lease events will remove them from the Upcoming Lease Events List but will still be present in the Ignored Lease Events and Lease Events List";
    }

    return "";
  };

  const renderApplyButtonText = () => {
    switch (actionType) {
      case "snooze":
        return "Snooze";

      case "ignore":
        return "Ignore";

      case "unsnooze":
        return "Unsnooze";

      case "unignore":
        return "Unignore";
    }

    return "LEASE EVENTS";
  };

  const handleApply = () => {
    let settings = {};

    if (actionType === "snooze") {
      settings.snoozedUntil = snoozedUntil.toISOString();
    }

    if (actionType === "snooze" || actionType === "ignore") {
      settings.snoozeIgnoreComment = snoozeIgnoreComment;
    }

    settings.selectedLeaseEvents = leaseEventsToSnoozeIgnore;

    onApply(settings);
  };

  const handleClear = () => {
    if (actionType === "snooze") {
      setSnoozedUntil(new moment());
    }

    if (actionType === "snooze" || actionType === "ignore") {
      setSnoozeIgnoreComment("");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Container>
        <Box sx={style}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 15,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {renderTitle()}
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            <p>{renderSubtitle()}</p>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <LeaseEventTable
                  leaseEvents={leaseEventsToSnoozeIgnore}
                  totalLeaseEvents={leaseEventsToSnoozeIgnore.length}
                  hideColumns={[
                    "country",
                    "stateProvince",
                    "city",
                    "rentableBuildingArea",
                    "expireDate",
                    "associatedTransactions",
                  ]}
                  hideToolbar={true}
                  showCheckbox={false}
                  actionIcons={["delete"]}
                  onDelete={handleDelete}
                  pageSize={10}                  
                  onPageChange={()=>{}}
                  parent="snooze"
                />
              </Grid>
              {actionType === "snooze" && (
                <>
                  <Grid item xs={5}>
                    Snooze Until <span style={{ color: "red" }}>*</span>
                  </Grid>
                  <Grid item xs={7}>
                    <LocalizationProvider
                      dateAdapter={AdapterMoment}
                      style={{ width: "100%" }}
                    >
                      <DatePicker
                        disableMaskedInput
                        value={snoozedUntil}
                        format="DD MMM YYYY"
                        onChange={(newSnoozedUntil) => {
                          setSnoozedUntil(newSnoozedUntil);
                        }}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}
              {(actionType === "snooze" || actionType === "ignore") && (
                <>
                  <Grid item xs={5}>
                    {actionType === "snooze" ? "Snooze" : "Ignore"} Comment{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Grid>
                  <Grid item xs={7}>
                    <TextField
                      multiline
                      rows={4}
                      value={snoozeIgnoreComment}
                      placeholder={`Enter a comment on why you would like to ${
                        actionType === "snooze" ? "snooze" : "ignore"
                      } these lease events`}
                      onChange={(event) =>
                        setSnoozeIgnoreComment(event.target.value)
                      }
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <ButtonContainer>
                  <Button
                    className="apply"
                    variant="contained"
                    style={{
                      backgroundColor: colors.coreColors.jllRed.primary,
                    }}
                    disabled={
                      leaseEventsToSnoozeIgnore.length === 0 ||
                      ((actionType === "snooze" || actionType === "ignore") &&
                        snoozeIgnoreComment.length === 0)
                    }
                    onClick={handleApply}
                  >
                    {renderApplyButtonText()}
                  </Button>
                  <Button
                    className="clear"
                    variant="contained"
                    style={{ backgroundColor: "grey" }}
                    disabled={leaseEventsToSnoozeIgnore.length === 0}
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                </ButtonContainer>
              </Grid>
            </Grid>
          </Typography>
        </Box>
      </Container>
    </Modal>
  );
};

export default SnoozeIgnoreModal;
